import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  MapPin, 
  LogOut, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Users,
  FileText,
  Menu,
  X,
  RefreshCw,
  MoreVertical,
  UserCheck,
  UserX,
  Download,
  Bell,
  BellOff,
  FolderOpen,
  Settings
} from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { usePushNotifications } from '../hooks/usePushNotifications';
import WorkerRequests from '../components/WorkerRequests';
import WorkerExpenses from '../components/WorkerExpenses';
import WorkerMonthlySummary from '../components/WorkerMonthlySummary';
import AdminRequests from '../components/AdminRequests';
import WorkerDailyLog from '../components/WorkerDailyLog';
import AdminWorkLogs from '../components/AdminWorkLogs';
import WorkOrderForm from '../components/WorkOrderForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icons
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 24px; height: 24px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeWorkers, setActiveWorkers] = useState([]);
  const [myStatus, setMyStatus] = useState({ is_checked_in: false, last_record: null });
  const [myHistory, setMyHistory] = useState([]);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [whatsappLink, setWhatsappLink] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [payslipMonths, setPayslipMonths] = useState([]);
  const [absentData, setAbsentData] = useState(null);
  const [clientsList, setClientsList] = useState([]);
  const { isSupported: pushSupported, isSubscribed: pushSubscribed, permission: pushPermission, isLoading: pushLoading, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe } = usePushNotifications();

  const isAdmin = user?.role === 'admin';
  const isSupervisor = user?.role === 'supervisor';
  const canViewAll = isAdmin || isSupervisor;

  // Get current location
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocalizzazione non supportata dal browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
        setLocationError(null);
      },
      (error) => {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationError('Permesso di geolocalizzazione negato');
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError('Posizione non disponibile');
            break;
          case error.TIMEOUT:
            setLocationError('Timeout richiesta posizione');
            break;
          default:
            setLocationError('Errore geolocalizzazione');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    setRefreshing(true);
    try {
      // Get my status
      const statusRes = await axios.get(`${API_URL}/api/attendance/status`, { withCredentials: true });
      setMyStatus(statusRes.data);

      // Get my history
      const historyRes = await axios.get(`${API_URL}/api/attendance/my?limit=10`, { withCredentials: true });
      setMyHistory(historyRes.data);

      // Get active workers if admin/supervisor
      if (canViewAll) {
        const workersRes = await axios.get(`${API_URL}/api/attendance/active-workers`, { withCredentials: true });
        setActiveWorkers(workersRes.data);
      }

      // Get WhatsApp link
      const whatsappRes = await axios.get(`${API_URL}/api/share/whatsapp-link`, { withCredentials: true });
      setWhatsappLink(whatsappRes.data.whatsapp_url);

      // Get available payslip months for workers
      if (!isAdmin && !isSupervisor) {
        try {
          const payslipRes = await axios.get(`${API_URL}/api/worker/my-payslip-months`, { withCredentials: true });
          setPayslipMonths(payslipRes.data);
        } catch (e) { /* silenced */ }
      }

      // Get absent workers for admin
      if (isAdmin) {
        try {
          const absentRes = await axios.get(`${API_URL}/api/admin/absent-today`, { withCredentials: true });
          setAbsentData(absentRes.data);
        } catch (e) { /* silenced */ }
      }

      // Fetch clients list for work order form
      try {
        const clientsRes = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
        setClientsList(clientsRes.data);
      } catch (e) { /* silenced */ }
    } catch (error) {
      // silenced
    } finally {
      setRefreshing(false);
    }
  }, [canViewAll, isAdmin, isSupervisor]);

  useEffect(() => {
    getCurrentLocation();
    fetchData();

    // Refresh data every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [getCurrentLocation, fetchData]);

  // Handle check-in/check-out
  const handleAttendance = async (action) => {
    setIsCheckingIn(true);
    
    // Use current location or fallback to (0,0) if GPS unavailable
    let loc = currentLocation;
    if (!loc) {
      // Try one more time to get location
      try {
        loc = await new Promise((resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error('no-geo'));
            return;
          }
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
            () => resolve(null),
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
          );
        });
      } catch {
        loc = null;
      }
      if (loc) {
        setCurrentLocation(loc);
        setLocationError(null);
      }
    }

    // If still no location, proceed with (0,0) — position will show as "Manuale" in reports
    const latitude = loc?.latitude || 0;
    const longitude = loc?.longitude || 0;

    try {
      await axios.post(`${API_URL}/api/attendance`, {
        latitude,
        longitude,
        action: action
      }, { withCredentials: true });

      if (!loc) {
        setLocationError('Check-in registrato senza posizione GPS. Abilita la geolocalizzazione nelle impostazioni.');
      }
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Errore durante la registrazione';
      alert(msg);
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Map center - default to Italy if no workers
  const mapCenter = activeWorkers.find(w => w.is_checked_in && w.last_location)?.last_location
    ? [activeWorkers.find(w => w.is_checked_in && w.last_location).last_location.latitude, 
       activeWorkers.find(w => w.is_checked_in && w.last_location).last_location.longitude]
    : currentLocation 
    ? [currentLocation.latitude, currentLocation.longitude]
    : [41.9028, 12.4964];

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-[#E4E4E7] h-16 sm:h-20 flex items-center px-4 sm:px-8">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-10 h-10 bg-[#0055FF] rounded-lg flex items-center justify-center">
            <MapPin className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-[#09090B]">Presenze Operai</h1>
            <p className="text-xs text-[#71717A] hidden sm:block">
              {user?.name} {user?.surname} • {user?.role === 'admin' ? 'Amministratore' : user?.role === 'supervisor' ? 'Supervisore' : 'Operaio'}
            </p>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-4">
          {canViewAll && (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate('/workers')}
                data-testid="nav-workers-btn"
                className="text-[#09090B] hover:bg-[#F4F4F5]"
              >
                <Users className="w-4 h-4 mr-2" />
                Operai
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/reports')}
                data-testid="nav-reports-btn"
                className="text-[#09090B] hover:bg-[#F4F4F5]"
              >
                <FileText className="w-4 h-4 mr-2" />
                Report
              </Button>
              <Button
                variant="ghost"
                onClick={async () => {
                  try {
                    const res = await axios.post(`${API_URL}/api/admin/seed-data`, {}, { withCredentials: true });
                    alert(`Dati caricati!\nOperai: ${res.data.created_workers}\nPresenze: ${res.data.created_attendance}\nPagamenti: ${res.data.created_payments}\nAcconti: ${res.data.created_acconti}`);
                    window.location.reload();
                  } catch (e) {
                    alert('Errore: ' + (e.response?.data?.detail || e.message));
                  }
                }}
                data-testid="seed-data-btn"
                className="text-[#0055FF] hover:bg-[#0055FF]/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Carica Dati
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            onClick={() => navigate('/clients')}
            data-testid="nav-clients-btn"
            className="text-[#09090B] hover:bg-[#F4F4F5]"
          >
            <FolderOpen className="w-4 h-4 mr-2" />
            Clienti
          </Button>
          {isAdmin && (
            <Button
              variant="ghost"
              onClick={() => navigate('/settings')}
              data-testid="nav-settings-btn"
              className="text-[#09090B] hover:bg-[#F4F4F5]"
            >
              <Settings className="w-4 h-4 mr-2" />
              Impostazioni
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="text-[#FF3B30] hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Esci
          </Button>
        </nav>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden p-2 text-[#09090B]"
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden bg-white border-b border-[#E4E4E7] px-4 py-4 space-y-2"
          >
            {canViewAll && (
              <>
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/workers'); setMobileMenuOpen(false); }}
                  className="w-full justify-start text-[#09090B]"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Gestione Operai
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/reports'); setMobileMenuOpen(false); }}
                  className="w-full justify-start text-[#09090B]"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Report
                </Button>
                <Button
                  variant="ghost"
                  onClick={async () => {
                    setMobileMenuOpen(false);
                    try {
                      const res = await axios.post(`${API_URL}/api/admin/seed-data`, {}, { withCredentials: true });
                      alert(`Dati caricati!\nOperai: ${res.data.created_workers}\nPresenze: ${res.data.created_attendance}\nPagamenti: ${res.data.created_payments}\nAcconti: ${res.data.created_acconti}`);
                      window.location.reload();
                    } catch (e) {
                      alert('Errore: ' + (e.response?.data?.detail || e.message));
                    }
                  }}
                  className="w-full justify-start text-[#0055FF]"
                  data-testid="mobile-seed-data-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Carica Dati
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              onClick={() => { navigate('/clients'); setMobileMenuOpen(false); }}
              className="w-full justify-start text-[#09090B]"
              data-testid="mobile-nav-clients-btn"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Clienti
            </Button>
            {isAdmin && (
              <Button
                variant="ghost"
                onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }}
                className="w-full justify-start text-[#09090B]"
                data-testid="mobile-nav-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Impostazioni
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-[#FF3B30]"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Esci
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="p-4 sm:p-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-[#09090B]">Dashboard</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={refreshing}
            data-testid="refresh-btn"
            className="border-[#E4E4E7]"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>

        <div className={`grid gap-6 ${canViewAll ? 'lg:grid-cols-2' : 'lg:grid-cols-1'}`}>
          {/* Admin Pending Requests */}
          {isAdmin && <AdminRequests />}
          {isAdmin && <AdminWorkLogs />}

          {/* Check-in/Check-out Card */}
          <Card className="border-[#E4E4E7] shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-[#09090B] flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#0055FF]" />
                Registra Presenza
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status Display */}
              <div className={`p-4 rounded-lg ${myStatus.is_checked_in ? 'bg-green-50 border border-green-200' : 'bg-gray-50 border border-[#E4E4E7]'}`}>
                <div className="flex items-center gap-3">
                  {myStatus.is_checked_in ? (
                    <CheckCircle className="w-6 h-6 text-[#34C759]" />
                  ) : (
                    <XCircle className="w-6 h-6 text-[#71717A]" />
                  )}
                  <div>
                    <p className="font-semibold text-[#09090B]">
                      {myStatus.is_checked_in ? 'Sei in servizio' : 'Non in servizio'}
                    </p>
                    {myStatus.last_record && (
                      <p className="text-sm text-[#71717A]">
                        Ultimo {myStatus.last_record.action === 'check_in' ? 'ingresso' : 'uscita'}: {formatDate(myStatus.last_record.timestamp)}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Location Status */}
              {locationError ? (
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-700">
                  <p>{locationError}</p>
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant="link"
                      size="sm"
                      onClick={getCurrentLocation}
                      className="text-[#0055FF] p-0 h-auto"
                    >
                      Riprova GPS
                    </Button>
                    <span className="text-xs text-[#71717A]">• Puoi comunque registrare entrata/uscita</span>
                  </div>
                </div>
              ) : currentLocation ? (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-[#0055FF] flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Posizione rilevata
                </div>
              ) : (
                <div className="p-3 bg-gray-50 border border-[#E4E4E7] rounded-lg text-sm text-[#71717A]">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 animate-pulse" />
                    Rilevamento posizione...
                  </div>
                  <p className="text-xs mt-1">Puoi comunque registrare entrata/uscita</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <Button
                  onClick={() => handleAttendance('check_in')}
                  disabled={isCheckingIn || myStatus.is_checked_in}
                  data-testid="checkin-btn"
                  className="bg-[#34C759] hover:bg-[#2DB84D] text-white font-semibold py-6 text-lg"
                >
                  {isCheckingIn ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    </span>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Entrata
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleAttendance('check_out')}
                  disabled={isCheckingIn || !myStatus.is_checked_in}
                  data-testid="checkout-btn"
                  className="bg-[#FF3B30] hover:bg-[#E63329] text-white font-semibold py-6 text-lg"
                >
                  {isCheckingIn ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    </span>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 mr-2" />
                      Uscita
                    </>
                  )}
                </Button>
              </div>

              {/* WhatsApp Share */}
              {whatsappLink && (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-share-btn"
                  className="flex items-center justify-center gap-2 p-3 bg-[#25D366] hover:bg-[#1DA851] text-white rounded-md font-semibold transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5" />
                  Condividi su WhatsApp
                </a>
              )}

              {/* Payslip Download Section - Workers only */}
              {!isAdmin && !isSupervisor && payslipMonths.length > 0 && (
                <div className="mt-4 pt-4 border-t border-[#E4E4E7]">
                  <h3 className="text-sm font-semibold text-[#09090B] mb-3 flex items-center gap-2">
                    <Download className="w-4 h-4 text-[#0055FF]" />
                    Le Tue Buste Paga
                  </h3>
                  <div className="space-y-2">
                    {payslipMonths.map((pm) => {
                      const monthNames = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];
                      return (
                        <button
                          key={`${pm.year}-${pm.month}`}
                          onClick={async () => {
                            try {
                              const response = await axios.get(`${API_URL}/api/worker/my-payslip`, {
                                params: { year: pm.year, month: pm.month },
                                withCredentials: true,
                                responseType: 'blob'
                              });
                              const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `Busta_Paga_${monthNames[pm.month-1]}_${pm.year}.pdf`);
                              link.style.display = 'none';
                              document.body.appendChild(link);
                              setTimeout(() => { link.click(); setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 200); }, 100);
                            } catch (e) {
                              alert('Errore nel download della busta paga');
                            }
                          }}
                          data-testid={`download-payslip-${pm.year}-${pm.month}`}
                          className="w-full flex items-center justify-between p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors text-left"
                        >
                          <span className="font-medium text-[#09090B]">{monthNames[pm.month-1]} {pm.year}</span>
                          <Download className="w-4 h-4 text-[#0055FF]" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Push Notifications Toggle */}
              {pushSupported && (
                <div className="mt-4 pt-4 border-t border-[#E4E4E7]">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {pushSubscribed ? (
                        <Bell className="w-4 h-4 text-[#34C759]" />
                      ) : (
                        <BellOff className="w-4 h-4 text-[#71717A]" />
                      )}
                      <div>
                        <p className="text-sm font-semibold text-[#09090B]">Notifiche Push</p>
                        <p className="text-xs text-[#71717A]">
                          {pushSubscribed ? 'Riceverai promemoria check-in/out' : 'Attiva per ricevere promemoria'}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant={pushSubscribed ? 'outline' : 'default'}
                      onClick={pushSubscribed ? pushUnsubscribe : pushSubscribe}
                      disabled={pushLoading || pushPermission === 'denied'}
                      data-testid="push-toggle-btn"
                      className={pushSubscribed ? 'border-[#FF3B30] text-[#FF3B30] hover:bg-red-50' : 'bg-[#34C759] hover:bg-[#2DB84D]'}
                    >
                      {pushLoading ? '...' : pushSubscribed ? 'Disattiva' : 'Attiva'}
                    </Button>
                  </div>
                  {pushPermission === 'denied' && (
                    <p className="text-xs text-[#FF3B30] mt-2">
                      Notifiche bloccate. Abilita dalle impostazioni del browser.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Worker-only sections */}
          {!isAdmin && !isSupervisor && (
            <>
              <WorkOrderForm clients={clientsList} onCreated={fetchData} />
              <WorkerDailyLog />
              <WorkerMonthlySummary />
              <WorkerRequests />
              <WorkerExpenses />
            </>
          )}

          {/* Admin Work Orders Section */}
          {isAdmin && (
            <>
            <Card className="border-[#E4E4E7] shadow-sm" data-testid="admin-work-orders-section">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg font-bold text-[#09090B] flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#0055FF]" />
                    Buoni di Lavoro Recenti
                  </span>
                  <Button size="sm" variant="outline" onClick={() => navigate('/clients')} data-testid="goto-clients-btn">
                    <FolderOpen className="w-4 h-4 mr-1" /> Clienti
                  </Button>
                </CardTitle>
              </CardHeader>
            </Card>
            </>
          )}

          {/* Map Card (Admin/Supervisor) */}
          {isAdmin && absentData && (
            <Card className="border-[#E4E4E7] shadow-sm" data-testid="absence-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-bold text-[#09090B] flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-[#FF9500]" />
                    Presenze Oggi — {absentData.date}
                  </span>
                  <span className="text-xs font-normal text-[#71717A]">
                    {absentData.present_count}/{absentData.total_workers} presenti
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-[#34C759]/10 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-[#34C759]">{absentData.present_count}</p>
                    <p className="text-xs text-[#71717A]">Presenti</p>
                  </div>
                  <div className={`rounded-lg p-3 text-center ${absentData.absent_count > 0 ? 'bg-[#FF3B30]/10' : 'bg-[#F4F4F5]'}`}>
                    <p className={`text-2xl font-bold ${absentData.absent_count > 0 ? 'text-[#FF3B30]' : 'text-[#71717A]'}`}>{absentData.absent_count}</p>
                    <p className="text-xs text-[#71717A]">Assenti</p>
                  </div>
                </div>
                {absentData.absent_count > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-[#FF3B30] uppercase tracking-wide">Non hanno fatto check-in:</p>
                    {absentData.absent.map((w) => (
                      <div key={w.user_id} className="flex items-center gap-2 bg-[#FF3B30]/5 rounded-md px-3 py-1.5" data-testid={`absent-worker-${w.user_id}`}>
                        <XCircle className="w-3.5 h-3.5 text-[#FF3B30] flex-shrink-0" />
                        <span className="text-sm font-medium text-[#09090B]">{w.name} {w.surname}</span>
                      </div>
                    ))}
                  </div>
                )}
                {absentData.absent_count === 0 && (
                  <div className="flex items-center gap-2 text-[#34C759] bg-[#34C759]/5 rounded-md px-3 py-2">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-semibold">Tutti presenti!</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Map Card (Admin/Supervisor) */}
          {canViewAll && (
            <Card className="border-[#E4E4E7] shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-bold text-[#09090B] flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[#0055FF]" />
                    Mappa Operai
                  </span>
                  <span className="text-sm font-normal text-[#71717A]">
                    {activeWorkers.filter(w => w.is_checked_in).length} attivi
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] rounded-lg overflow-hidden border border-[#E4E4E7]">
                  <MapContainer
                    center={mapCenter}
                    zoom={10}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {activeWorkers
                      .filter(w => w.is_checked_in && w.last_location)
                      .map((worker) => (
                        <Marker
                          key={worker.user_id}
                          position={[worker.last_location.latitude, worker.last_location.longitude]}
                          icon={createCustomIcon('#34C759')}
                        >
                          <Popup>
                            <div className="font-semibold">{worker.name} {worker.surname}</div>
                            <div className="text-sm text-gray-600">
                              Check-in: {worker.last_check_in ? formatDate(worker.last_check_in) : 'N/A'}
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    {currentLocation && (
                      <Marker
                        position={[currentLocation.latitude, currentLocation.longitude]}
                        icon={createCustomIcon('#0055FF')}
                      >
                        <Popup>La tua posizione</Popup>
                      </Marker>
                    )}
                  </MapContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Recent Activity */}
        <Card className="border-[#E4E4E7] shadow-sm mt-6">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl font-bold text-[#09090B] flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#0055FF]" />
              Le Mie Presenze Recenti
            </CardTitle>
          </CardHeader>
          <CardContent>
            {myHistory.length === 0 ? (
              <p className="text-center text-[#71717A] py-8">Nessuna presenza registrata</p>
            ) : (
              <div className="space-y-3">
                {myHistory.map((record, index) => (
                  <motion.div
                    key={record.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-center justify-between p-3 bg-[#F4F4F5] rounded-lg hover:bg-[#E4E4E7] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {record.action === 'check_in' ? (
                        <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-4 h-4 text-[#34C759]" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                          <XCircle className="w-4 h-4 text-[#FF3B30]" />
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-[#09090B]">
                          {record.action === 'check_in' ? 'Entrata' : 'Uscita'}
                        </p>
                        <p className="text-sm text-[#71717A]">{formatDate(record.timestamp)}</p>
                      </div>
                    </div>
                    <div className="text-right text-sm text-[#71717A]">
                      <MapPin className="w-3 h-3 inline mr-1" />
                      {record.latitude.toFixed(4)}, {record.longitude.toFixed(4)}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Active Workers List (Admin/Supervisor) */}
        {canViewAll && (
          <Card className="border-[#E4E4E7] shadow-sm mt-6">
            <CardHeader className="pb-4">
              <CardTitle className="text-xl font-bold text-[#09090B] flex items-center gap-2">
                <Users className="w-5 h-5 text-[#0055FF]" />
                Stato Operai
              </CardTitle>
            </CardHeader>
            <CardContent>
              {activeWorkers.length === 0 ? (
                <p className="text-center text-[#71717A] py-8">Nessun operaio registrato</p>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {activeWorkers.map((worker, index) => (
                    <motion.div
                      key={worker.user_id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`p-4 rounded-lg border ${worker.is_checked_in ? 'bg-green-50 border-green-200' : 'bg-white border-[#E4E4E7]'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${worker.is_checked_in ? 'bg-[#34C759]' : 'bg-[#71717A]'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-[#09090B] truncate">
                            {worker.name} {worker.surname}
                          </p>
                          <p className="text-xs text-[#71717A]">
                            {worker.is_checked_in ? 'In servizio' : 'Non in servizio'}
                          </p>
                        </div>
                        {isAdmin && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`worker-menu-${worker.user_id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {!worker.is_checked_in ? (
                                <DropdownMenuItem
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API_URL}/api/admin/force-checkin/${worker.user_id}`, {}, { withCredentials: true });
                                      fetchData();
                                    } catch (e) {
                                      // silenced
                                    }
                                  }}
                                  className="text-[#34C759] cursor-pointer"
                                  data-testid={`force-checkin-${worker.user_id}`}
                                >
                                  <UserCheck className="w-4 h-4 mr-2" />
                                  Forza Entrata
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  onClick={async () => {
                                    try {
                                      await axios.post(`${API_URL}/api/admin/force-checkout/${worker.user_id}`, {}, { withCredentials: true });
                                      fetchData();
                                    } catch (e) {
                                      // silenced
                                    }
                                  }}
                                  className="text-[#FF3B30] cursor-pointer"
                                  data-testid={`force-checkout-${worker.user_id}`}
                                >
                                  <UserX className="w-4 h-4 mr-2" />
                                  Forza Uscita
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
